import React from 'react'
import { Link, NavLink } from 'react-router'
import { Box, Stack, TableCell, TableRow } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../../i18n/localeUtils'

import { ConvertibleEntity } from 'types/ConvertibleEntity'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../../../packages/utils/numberFormatters'
import { ConvertibleAction } from 'types/ConvertibleAction'
import { KVLabel } from '../../../../components/entities/IssueTypeLabels'
import { getConvertibleNoncashConsiderationLabel } from '../../../../components/utils/convertibleUtils'

type ConvertibleObject = {
    type: 'issue'
    subStartDate: string
    subEndDate: string
    item: ConvertibleEntity
}

const ConvertibleTableRow = ({
    obj,
    isLargeScreen,
    multipleInstrumentsExists = false,
}: {
    obj: ConvertibleObject
    isLargeScreen: boolean
    multipleInstrumentsExists: boolean
}) => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    const convertible = obj.item
    const noncashConsiderationLabel = getConvertibleNoncashConsiderationLabel(convertible)

    const convertibleData = convertible.convertibleActions.reduce(
        (acc: { loanAmount: number; returnedAmount: number }, action: ConvertibleAction) => {
            if (action.actionType === 'new_loan' && action.loanAmount !== null) {
                acc.loanAmount += action.loanAmount
            } else if (action.repayment !== null) {
                acc.returnedAmount += action.repayment
            } else if (action.conversionPrice !== null && action.convertedShares !== null) {
                acc.returnedAmount += action.conversionPrice * action.convertedShares
            }
            return acc
        },
        { loanAmount: 0, returnedAmount: 0 },
    )

    const remainingAmount = convertible.completed
        ? 0
        : Math.max(convertibleData.loanAmount - convertibleData.returnedAmount, 0)

    return (
        <TableRow>
            {multipleInstrumentsExists && isLargeScreen ? <TableCell /> : null}
            <TableCell>
                <Stack
                    direction={{ sx: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{
                        alignItems: { xs: 'start', sm: 'center' },
                    }}
                >
                    <Box>
                        <KVLabel />
                    </Box>
                    <Link component={NavLink} to={`/convertibles/${convertible.id}`}>
                        {convertible.convertibleName}
                        {noncashConsiderationLabel ? ` (${noncashConsiderationLabel})` : null}
                    </Link>
                </Stack>
            </TableCell>
            <TableCell>{formatDateYYYYMMDD(convertible.maturityDate, { locale })}</TableCell>
            <TableCell>
                {convertible.fixedConversionPrice ? (
                    formatMoney(convertible.fixedConversionPrice, {
                        minDecimals: 2,
                        maxDecimals: 5,
                        locale,
                    })
                ) : (
                    <div>
                        <div>
                            {t('site.pricing.vwapDiscount')}{' '}
                            {formatPercentage(convertible?.vwapDiscount, {
                                locale,
                                minDecimals: 0,
                            })}
                        </div>
                        <div>
                            {t('site.pricing.min')}{' '}
                            {formatMoney(convertible?.priceMin, {
                                locale,
                                currencySymbol: convertible.currency,
                                minDecimals: 2,
                                maxDecimals: 5,
                            }) || t('site.pricing.parValue')}
                            {convertible?.priceMax && (
                                <>
                                    , {t('site.pricing.max')}{' '}
                                    {formatMoney(convertible?.priceMax, {
                                        locale,
                                        currencySymbol: convertible.currency,
                                        minDecimals: 2,
                                        maxDecimals: 5,
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </TableCell>
            {isLargeScreen ? (
                <TableCell>
                    {convertible.completed
                        ? `(${formatBigMoney(convertibleData.loanAmount, { locale })})`
                        : formatBigMoney(remainingAmount, { locale })}
                </TableCell>
            ) : null}
        </TableRow>
    )
}

export default ConvertibleTableRow
